import { HStack, Badge, Icon, MenuItem, IconButton } from '@chakra-ui/react';
import { LucideX } from 'lucide-react';
import { UserRole } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';
import { useCallback } from 'react';

const roleColorSchemes: Record<UserRole, string> = {
  NATIONAL_MANAGER: 'blue',
  REGIONAL_MANAGER: 'orange',
  STATE_MANAGER: 'yellow',
  SITE_MANAGER: 'green',
  CONSULTANT: 'purple',
  ADMIN: 'blue',
  STUDENT: 'purple',
  ASSESSOR: 'orange',
};

// Many different sets of roles are handled by this component, so it only accepts
// string roles. It's up to you when the role is passed back to `onRoleClick` to
// use Zod to validate the role.
export function RoleMenuItem({
  role,
  isSelected,
  onRoleClick,
}: {
  role: string;
  isSelected: boolean;
  onRoleClick: (role: string | undefined) => void;
}) {
  const handleRoleClick = useCallback(() => {
    onRoleClick(isSelected ? undefined : role); // Toggle between role and undefined on click
  }, [isSelected, role, onRoleClick]);

  const handleRemoveFilterClick = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      onRoleClick(undefined); // Set role to undefined when LucideX is clicked
    },
    [onRoleClick],
  );

  return (
    <MenuItem bgColor={isSelected ? `blue.100` : undefined} onClick={handleRoleClick}>
      <HStack w="full">
        <Badge
          borderRadius="full"
          colorScheme={roleColorSchemes[role] || 'gray'}
          variant="subtle"
          textTransform="none"
        >
          {role.replace(/_/g, ' ')}
        </Badge>

        {isSelected ? (
          <IconButton
            as="span"
            aria-label="remove filter"
            variant="ghost"
            ml="auto"
            onClick={handleRemoveFilterClick}
          >
            <Icon as={LucideX} color={'gray.500'} boxSize="4" />
          </IconButton>
        ) : null}
      </HStack>
    </MenuItem>
  );
}
